import React, { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Check if already dismissed
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) return;

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Listen for beforeinstallprompt (Android/Chrome)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show after delay
    if (ios && !navigator.standalone) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', handler); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 pb-safe animate-in slide-in-from-bottom" data-testid="install-prompt">
      <div className="max-w-md mx-auto bg-slate-900 border border-cyan-500/30 rounded-2xl p-4 shadow-2xl shadow-cyan-500/10">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <Download className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-white">Installer SecurESSI-AI</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {isIOS
                ? "Appuyez sur Partager puis \"Sur l'ecran d'accueil\""
                : "Installez l'app pour un acces rapide"
              }
            </p>
          </div>
          <button onClick={handleDismiss} className="p-1 text-slate-500 hover:text-white" data-testid="install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-3 flex gap-2">
          {isIOS ? (
            <div className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white/5 rounded-xl text-xs text-slate-300">
              <Share className="w-4 h-4" /> Partager &rarr; Ecran d'accueil
            </div>
          ) : (
            <button
              onClick={handleInstall}
              className="flex-1 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold rounded-xl active:scale-95 transition-transform"
              data-testid="install-btn"
            >
              Installer
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="px-4 py-2.5 bg-white/5 text-slate-400 text-sm rounded-xl"
            data-testid="install-later"
          >
            Plus tard
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
